import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useAuthStore, type User } from '../stores/authStore';

const loginScopes = ['openid', 'profile', 'email', 'User.Read', 'Files.Read'];
const entraTenantId =
  import.meta.env.VITE_ENTRA_TENANT_ID ?? '18a2c300-b493-4837-a293-015508c28170';
const entraClientId =
  import.meta.env.VITE_ENTRA_CLIENT_ID ?? 'f7e0e8e5-6cc3-4908-8031-7754c87ac987';
const entraRedirectUri =
  import.meta.env.VITE_ENTRA_REDIRECT_URI ??
  (typeof window !== 'undefined'
    ? window.location.origin
    : 'https://ally-v2-vnet.jollywave-0e412d60.westus.azurecontainerapps.io');

const msalConfig = {
  auth: {
    clientId: entraClientId,
    authority: `https://login.microsoftonline.com/${entraTenantId}`,
    redirectUri: entraRedirectUri,
  },
  cache: {
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Authentication failed';
}

function normalizeUser(input: any): User {
  const roles = Array.isArray(input?.roles)
    ? input.roles
    : input?.role
      ? [input.role]
      : ['user'];

  return {
    id: String(input?.id ?? crypto.randomUUID()),
    name: String(input?.name ?? input?.displayName ?? input?.email ?? 'AFN User'),
    email: String(input?.email ?? ''),
    avatar: input?.avatar ?? undefined,
    roles: roles.map((role: unknown) => String(role).toUpperCase()),
  };
}

async function exchangeToken(accessToken: string): Promise<User> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ accessToken }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.success || !payload?.user) {
    throw new Error(payload?.error || 'Microsoft sign-in failed');
  }

  return normalizeUser(payload.user);
}

async function acquireAccessToken(account: AccountInfo): Promise<string> {
  const result = await msalInstance.acquireTokenSilent({
    account,
    scopes: loginScopes,
  });

  return result.accessToken;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

function AuthProviderContent({ children }: AuthProviderProps) {
  const user = useAuthStore((state) => state.user);
  const isLoadingFromStore = useAuthStore((state) => state.isLoading);
  const loginWithToken = useAuthStore((state) => state.loginWithToken);
  const clearSession = useAuthStore((state) => state.clearSession);
  const setLoading = useAuthStore((state) => state.setLoading);

  const [error, setError] = useState<string | null>(null);

  const hydrateSession = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await msalInstance.initialize();

      const redirectResult = await msalInstance.handleRedirectPromise();
      const account =
        redirectResult?.account ?? msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

      if (!account) {
        clearSession();
        return;
      }

      msalInstance.setActiveAccount(account);

      const accessToken = redirectResult?.accessToken || (await acquireAccessToken(account));
      const portalUser = await exchangeToken(accessToken);
      loginWithToken(accessToken, portalUser);
    } catch (error) {
      console.error('Authentication initialization failed:', error);
      clearSession();
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [clearSession, loginWithToken, setLoading]);

  useEffect(() => {
    void hydrateSession();
  }, [hydrateSession]);

  const login = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      await msalInstance.initialize();
      await msalInstance.loginRedirect({
        scopes: loginScopes,
        prompt: 'select_account',
      });
    } catch (error) {
      console.error('Microsoft login failed:', error);
      setError(getErrorMessage(error));
      setLoading(false);
    }
  }, [setLoading]);

  const logout = useCallback(async () => {
    setError(null);
    clearSession();

    try {
      await msalInstance.initialize();
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri:
          import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI ?? window.location.origin,
      });
    } catch (error) {
      console.error('Microsoft logout failed:', error);
      setError(getErrorMessage(error));
      setLoading(false);
    }
  }, [clearSession, setLoading]);

  const getAccessToken = useCallback(async () => {
    try {
      await msalInstance.initialize();
      const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
      if (!account) {
        return null;
      }

      msalInstance.setActiveAccount(account);
      const accessToken = await acquireAccessToken(account);
      const currentUser = useAuthStore.getState().user;

      if (currentUser) {
        useAuthStore.getState().loginWithToken(accessToken, currentUser);
      }

      return accessToken;
    } catch (error) {
      console.error('Failed to acquire Microsoft access token:', error);
      return null;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading: isLoadingFromStore,
      error,
      login,
      logout,
      getAccessToken,
    }),
    [error, getAccessToken, isLoadingFromStore, login, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </MsalProvider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
